/**
 * De positiestroom, één keer, voor iedereen die hem nodig heeft.
 *
 * ------------------------------------------------------------------
 * Hier stond niets, en dat was het hele probleem. De mini app deed één
 * `getCurrentPosition` bij het openen en daarna nooit meer:
 *
 *   - je positie bevroor op de plek waar je de app opende;
 *   - `coords.heading` is per definitie null als je stilstaat, en dat is
 *     precies wanneer je een app opent — dus de koers bleef voor de hele
 *     sessie null, en het bord vroeg zijn meldingen zonder koers op;
 *   - zonder koers geeft de server álle rijbanen terug. Op de A10 richting
 *     noord stonden daardoor twee trajectcontroles van de zúidbaan als "op
 *     mijn route" op het scherm. Nagemeten op de echte server: mét
 *     `heading=0` vallen ze allebei weg.
 *   - en `coords.speed` werd nergens uitgelezen, dus een gemiddelde over een
 *     traject was per constructie onmogelijk.
 *
 * Twee snelheden, met opzet gescheiden:
 *
 *   LEVEND   elke fix, ongeveer één per seconde. Hier hangt de trajectmodule
 *            aan. Gaat bewust NIET door React-state van de hele app heen —
 *            dat zou het hele scherm elke seconde opnieuw tekenen.
 *   ANKER    de grove positie waarop het bord en de kaart hun verzoeken doen.
 *            Verschuift pas na ANKER_M meter of ANKER_MS milliseconden,
 *            anders zou elke meter een nieuw netwerkverzoek zijn.
 *
 * Een abonnement in plaats van state, zodat alleen de balk die het toont
 * opnieuw tekent en niet de rest van de app.
 */

const M_PER_DEG_LAT = 111320.0;

/** Hoeveel je verplaatst moet zijn voordat het bord opnieuw wordt opgehaald. */
export const ANKER_M = 150;

/** En hoe lang het anker hooguit oud mag zijn, ook als je stilstaat. */
export const ANKER_MS = 30000;

/** Hoeveel graden koersverschil een nieuw anker waard is. */
export const ANKER_KOERS_DEG = 25;

/**
 * Onder deze snelheid is `coords.heading` niet te vertrouwen.
 *
 * Zelfde grens als Android (LocationProvider.MIN_SPEED_FOR_BEARING_MS): onder
 * loopsnelheid draait de koers alle kanten op omdat hij uit twee opeenvolgende
 * posities komt die binnen de meetfout van elkaar liggen. Een koers die
 * rondtolt is erger dan geen koers: hij snoeit het bord op een richting die je
 * niet op rijdt.
 */
export const MIN_SNELHEID_VOOR_KOERS_MS = 1.5;

export function afstandM(aLat, aLon, bLat, bLon) {
  const mPerLon = M_PER_DEG_LAT * Math.cos((aLat * Math.PI) / 180);
  const dx = (bLon - aLon) * mPerLon;
  const dy = (bLat - aLat) * M_PER_DEG_LAT;
  return Math.sqrt(dx * dx + dy * dy);
}

/** Het kleinste verschil tussen twee koersen, 0..180. */
export const koersVerschil = (a, b) => Math.abs((((a - b) % 360) + 540) % 360 - 180);

let laatste = null;
const luisteraars = new Set();

/** De laatste fix, of null zolang er nog geen is. */
export const huidigeFix = () => laatste;

/**
 * Aanmelden voor elke fix. Geeft de afmeldfunctie terug.
 *
 * Wie zich aanmeldt krijgt meteen de laatste bekende fix, zodat een balk die
 * halverwege een rit op het scherm komt niet eerst een seconde leeg staat.
 */
export function opFix(fn) {
  luisteraars.add(fn);
  if (laatste) fn(laatste);
  return () => luisteraars.delete(fn);
}

/**
 * Een nieuwe fix binnen. Alleen aangeroepen door de watchPosition-lus.
 *
 * `nowMs` komt van buiten en niet uit Date.now() hierbinnen, zodat een test de
 * klok in de hand heeft — het gemiddelde over een traject is een deling door
 * tijd, en een module die zijn eigen klok leest is niet te toetsen.
 */
export function nieuweFix({ lat, lon, heading, speedMs, accuracyM, nowMs }) {
  const bruikbareKoers =
    heading != null && Number.isFinite(heading)
      && speedMs != null && speedMs > MIN_SNELHEID_VOOR_KOERS_MS
      ? heading
      : null;

  laatste = {
    lat,
    lon,
    // De koers blijft staan als hij even wegvalt: een enkele tik zonder
    // beweging (voor een stoplicht, in een file) hoort niet het hele bord van
    // richting te laten veranderen.
    heading: bruikbareKoers != null ? bruikbareKoers : (laatste ? laatste.heading : null),
    speedMs: speedMs != null && Number.isFinite(speedMs) ? speedMs : null,
    accuracyM: accuracyM != null && Number.isFinite(accuracyM) ? accuracyM : null,
    atMs: nowMs,
  };
  for (const fn of luisteraars) fn(laatste);
  return laatste;
}

/**
 * Of deze fix een nieuw anker verdient — dus of bord en kaart opnieuw moeten
 * worden opgehaald.
 *
 * Zuiver, want dit is de knop tussen "de app weet waar je bent" en "de app
 * belt de server plat".
 */
export function verdientAnker(anker, fix) {
  if (!anker) return true;
  if (fix.atMs - anker.atMs >= ANKER_MS) return true;
  if (afstandM(anker.lat, anker.lon, fix.lat, fix.lon) >= ANKER_M) return true;
  if (anker.heading == null && fix.heading != null) return true;
  if (anker.heading != null && fix.heading != null
      && koersVerschil(anker.heading, fix.heading) >= ANKER_KOERS_DEG) return true;
  return false;
}

/** Alleen voor tests: de stroom leegmaken. */
export function _reset() {
  laatste = null;
  luisteraars.clear();
}
